import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { VStack } from '@astryxdesign/core/VStack';
import { HStack } from '@astryxdesign/core/HStack';
import { Text } from '@astryxdesign/core/Text';
import { Button } from '@astryxdesign/core/Button';
import { TextInput } from '@astryxdesign/core/TextInput';
import { IconButton } from '@astryxdesign/core/IconButton';
import { Banner } from '@astryxdesign/core/Banner';
import { PaperPlaneRight, CalendarPlus } from '@phosphor-icons/react';
import { Sheet, useBackButton } from '../../components/Sheet';
import { useTenant } from '../../app/tenant-context';
import { publicApi } from '../../lib/public-api';

interface Msg { role: 'user' | 'assistant'; content: string; offline?: boolean }

const DEFAULT_CLIENT = ['Когда ближайшее окно?', 'Сколько стоит полировка?', 'Как найти студию?'];
const DEFAULT_OWNER = ['Что у меня завтра?', 'Сколько машин было на неделе?', 'Сколько денег получено?'];

function useChat(mode: 'client' | 'owner', getJwt?: () => Promise<string | undefined>) {
  const { slug } = useTenant();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const ask = useMutation({
    mutationFn: async (message: string) =>
      publicApi.assistant({ slug, mode, message, history: msgs.slice(-6).map(({ role, content }) => ({ role, content })) }, await getJwt?.()),
    onSuccess: (r) => setMsgs((m) => [...m, { role: 'assistant', content: r.reply, offline: r.mode === 'fallback' }]),
    onError: (e) => setMsgs((m) => [...m, { role: 'assistant', content: (e as Error).message, offline: true }]),
  });
  const send = (q: string) => {
    const t = q.trim();
    if (!t || ask.isPending) return false;
    setMsgs((m) => [...m, { role: 'user', content: t }]);
    ask.mutate(t);
    return true;
  };
  return { msgs, send, pending: ask.isPending };
}

export function ChatThread({ mode, chat, onBook }: { mode: 'client' | 'owner'; chat: ReturnType<typeof useChat>; onBook?: () => void }) {
  const { sf } = useTenant();
  const endRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [chat.msgs.length, chat.pending]);
  const examples = mode === 'owner' ? DEFAULT_OWNER : (sf.profile.assistant_examples.length ? sf.profile.assistant_examples : DEFAULT_CLIENT);
  return (
    <VStack gap={3}>
      {chat.msgs.length === 0 ? (
        <VStack gap={2}>
          <Text type="supporting">Например:</Text>
          <HStack gap={2} wrap="wrap">
            {examples.map((e) => <Button key={e} label={e} variant="secondary" size="sm" onClick={() => chat.send(e)} />)}
          </HStack>
        </VStack>
      ) : null}
      <VStack gap={2} aria-live="polite">
        {chat.msgs.map((m, i) => (
          <div key={i} className={`chat-bubble ${m.role === 'user' ? 'me' : 'bot'}`}><Text color="inherit">{m.content}</Text></div>
        ))}
        {chat.pending ? <div className="chat-bubble bot"><Text color="secondary">Смотрю данные…</Text></div> : null}
      </VStack>
      {chat.msgs.some((m) => m.offline) ? (
        <Banner status="info" title="Ответ собран напрямую из данных студии" description="Умный помощник сейчас недоступен — цифры и время настоящие." container="card" />
      ) : null}
      {onBook && chat.msgs.length > 0 ? <Button label="Записаться" icon={<CalendarPlus size={18} />} variant="primary" onClick={onBook} /> : null}
      <div ref={endRef} />
    </VStack>
  );
}

export function Composer({ chat }: { chat: ReturnType<typeof useChat> }) {
  const [text, setText] = useState('');
  const submit = () => { if (chat.send(text)) setText(''); };
  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <HStack gap={2} vAlign="end">
        <TextInput label="Сообщение" isLabelHidden value={text} onChange={setText} placeholder="Напишите вопрос" enterKeyHint="send" width="100%" />
        <IconButton label="Отправить" icon={<PaperPlaneRight size={20} weight="fill" />} variant="primary" isLoading={chat.pending} onClick={submit} />
      </HStack>
    </form>
  );
}

export { useChat };

export function AssistantSheet({ open, onClose, mode }: { open: boolean; onClose: () => void; mode: 'client' | 'owner' }) {
  const { openBooking } = useTenant();
  const chat = useChat(mode);
  useBackButton(open, () => false, onClose);
  return (
    <Sheet open={open} onClose={onClose} title="Помощник" description="Отвечает по услугам, ценам и свободному времени этой студии."
      footer={<Composer chat={chat} />}>
      <ChatThread mode={mode} chat={chat} onBook={() => { onClose(); window.setTimeout(() => openBooking(), 350); }} />
    </Sheet>
  );
}
